import { useCallback, useEffect, useRef, useState } from 'react'
import { blurFocusTimeout } from '../../../../utils/constants'
import { OptionItemInterface } from '../../../../types/interfaces'

export const useSearchSelectFilter = (
  list: OptionItemInterface[],
  onChangeHandler: (value: string) => void,
  value: string,
  visibleOptionsLength?: number,
) => {
  const [searchString, setSearchString] = useState<string>('')
  const [filteredList, setFilteredList] = useState<OptionItemInterface[]>(list)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [timeout] = useState<{
    value: ReturnType<typeof setTimeout> | undefined
  }>({
    value: undefined,
  })

  const divRefs = useRef<HTMLDivElement[] | HTMLInputElement[] | []>([])
  const divRef = useRef<HTMLInputElement | undefined>(null)
  const [focusedIndex, setFocusedIndex] = useState<number>(0)

  const handleKeyDown = useCallback(
    //@ts-ignore
    (focusedIndex: number, event: KeyboardEvent<HTMLDivElement>) => {
      timeout.value = setTimeout(() => {
        clearTimeout(timeout.value)
        timeout.value = undefined
      }, blurFocusTimeout)

      switch (event.key) {
        case 'Enter':
          filteredList[focusedIndex].value && onChangeHandler(filteredList[focusedIndex].value)
          setSearchString(list[focusedIndex].label)
          setExpanded(false)
          break
        case 'ArrowUp':
          setFocusedIndex((prevIndex) => (prevIndex > 1 ? prevIndex - 1 : prevIndex))
          break
        case 'ArrowDown':
          setFocusedIndex((prevIndex) =>
            prevIndex < divRefs.current.length - 1 ? prevIndex + 1 : prevIndex,
          )
          break
        default:
          break
      }
    },
    [setFocusedIndex, setSearchString, list, onChangeHandler, filteredList, timeout],
  )

  const createDivRef = (index: number) => (el: HTMLDivElement | HTMLInputElement) => {
    divRefs.current[index] = el
  }

  const onBlurAction = () => {
    if (!timeout.value) {
      timeout.value = setTimeout(() => {
        setExpanded(false)       
        clearTimeout(timeout.value)
        timeout.value = undefined
      }, blurFocusTimeout)
    }
  }

  useEffect(() => {
    searchString.length > 0 
      ? setFilteredList(
          list.filter((el) =>
            el.label.toLowerCase().includes(searchString.toString().toLowerCase()),
          ),
        )
      : setFilteredList(list.slice(0, visibleOptionsLength))
  }, [searchString, list, visibleOptionsLength])

  useEffect(() => {
    if (focusedIndex && divRefs.current && divRefs.current[focusedIndex]) {
      divRefs.current[focusedIndex].focus()
    }
  }, [focusedIndex])

  useEffect(() => {
    if (!value) setSearchString('')
  }, [value])

  return {
    filteredList,
    searchString,
    setSearchString,
    expanded,
    setExpanded,
    onBlurAction,
    createDivRef,
    setFocusedIndex,
    handleKeyDown,
    divRef
  }
}
